<template>
  <section class="my-instructors-widget">
    <div class="my-instructors-widget__header">
      <div class="my-instructors-widget__heading">
        <span class="my-instructors-widget__icon"><i class="uil-graduation-hat"></i></span>
        <h4>{{ displayTitle }}</h4>
      </div>
      <button
        v-if="hasMoreInstructors"
        type="button"
        class="my-instructors-widget__see-all"
        @click="showAllInstructors = !showAllInstructors"
      >
        <i class="uil-list-ui-alt" aria-hidden="true"></i>
        {{ showAllInstructors ? $t("general.close") : $t("home.see_all") }}
      </button>
    </div>

    <div class="my-instructors-widget__list">
      <button
        v-for="instructor in visibleInstructors"
        :key="instructor.id"
        type="button"
        class="instructor-card"
        @click="openInstructor(instructor)"
      >
        <img
          :src="instructor.photo"
          :alt="instructor.name"
          class="instructor-card__photo"
          @error="useFallbackPhoto"
        >
        <span class="instructor-card__content">
          <strong>{{ instructor.name }}</strong>
          <span v-if="instructor.title">{{ instructor.title }}</span>
          <span class="instructor-card__detail">{{ $t("general.detail") }}</span>
        </span>
      </button>
    </div>
    <hr>

    <instructor-detail-modal
      v-if="selectedInstructor"
      :instructor="selectedInstructor"
      @close="closeInstructor"
    />
  </section>
</template>

<script>
import noPhoto from "@/assets/images/no-photo.png";
import InstructorDetailModal from "@/view/User/Home/components/InstructorDetailModal.vue";
import instructorService from "@/core/services/instructor.service";

export default {
  name: "MyInstructorsWidget",
  components: {
    InstructorDetailModal,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    instructors: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedInstructor: null,
      instructorRequestId: 0,
      showAllInstructors: false,
    };
  },
  computed: {
    displayTitle() {
      return this.getLocalizedWidgetTitle(
        this.item,
        this.$t("general.my_instructors")
      );
    },
    hasMoreInstructors() {
      return this.instructors.length > 4;
    },
    visibleInstructors() {
      return this.showAllInstructors
        ? this.instructors
        : this.instructors.slice(0, 4);
    },
  },
  methods: {
    async openInstructor(instructor) {
      if (!instructor?.id) return;

      const requestId = ++this.instructorRequestId;
      this.selectedInstructor = instructor;

      try {
        const instructors = await instructorService.listMine();
        const detail = instructors.find(
          item => Number(item.id) === Number(instructor.id)
        );
        if (
          detail &&
          requestId === this.instructorRequestId &&
          Number(this.selectedInstructor?.id) === Number(instructor.id)
        ) {
          this.selectedInstructor = detail;
        }
      } catch (error) {
      }
    },
    closeInstructor() {
      this.instructorRequestId += 1;
      this.selectedInstructor = null;
    },
    useFallbackPhoto(event) {
      if (event.target.dataset.fallbackApplied) return;
      event.target.dataset.fallbackApplied = "true";
      event.target.src = noPhoto;
    },
  },
};
</script>

<style scoped>
.my-instructors-widget {
  width: 100%;
}

.my-instructors-widget__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.my-instructors-widget__heading {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.my-instructors-widget__header h4 {
  margin: 0;
  color: #333;
  font-family: var(--font-secondary);
  font-size: var(--text-lg);
  font-weight: 600;
}

.my-instructors-widget__icon {
  color: var(--primaryColor);
  font-size: 20px;
}

.my-instructors-widget__see-all {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  min-height: 40px;
  padding: 8px 16px;
  border: 0;
  border-radius: 8px;
  color: #333;
  background: transparent;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: 500;
  opacity: 0.7;
  outline: none;
  box-shadow: none;
  cursor: pointer;
}

.my-instructors-widget__see-all:focus,
.my-instructors-widget__see-all:focus-visible {
  outline: none;
  box-shadow: none;
}

@media (hover: hover) {
  .my-instructors-widget__see-all:hover {
    color: var(--iconColor);
    opacity: 0.8;
  }
}

.my-instructors-widget__list {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  padding-bottom: 8px;
}

.instructor-card {
  display: grid;
  grid-template-rows: 210px minmax(112px, auto);
  min-width: 0;
  padding: 0;
  overflow: hidden;
  border: 1px solid #dce3ef;
  border-radius: 12px;
  color: #29334a;
  background: #fff;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.instructor-card:hover,
.instructor-card:focus-visible {
  border-color: var(--primaryColor);
  box-shadow: 0 8px 20px rgba(27, 43, 78, 0.1);
  transform: translateY(-1px);
  outline: none;
}

.instructor-card__photo {
  width: 100%;
  height: 210px;
  object-fit: contain;
  background: #eef1f5;
}

.instructor-card__content {
  display: flex;
  min-width: 0;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  padding: 14px 16px;
}

.instructor-card__content strong,
.instructor-card__content > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.instructor-card__content > span {
  color: #7a8498;
  font-size: 12px;
}

.instructor-card__detail {
  color: var(--primaryColor) !important;
  font-weight: 600;
}

.my-instructors-widget hr {
  margin-top: 16px;
}

@media (max-width: 640px) {
  .my-instructors-widget__header {
    align-items: flex-start;
  }

  .my-instructors-widget__see-all {
    min-height: 32px;
    padding: 4px 8px;
  }

  .my-instructors-widget__list {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 960px) and (min-width: 641px) {
  .my-instructors-widget__list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
